import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { Form, message } from 'antd';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createTaskConsultation } from '@services/projects.service';
import { htmlToMarkdown } from '@utils/htmlToMarkdown.util';
import { contentToApiMentions } from '@utils/richTextMentions';
import { invalidateProjectQueries } from './projectQueryInvalidation';
import { getSprintTaskApiId, normalizePhaseLinkUrl } from '@hooks/projects/projectHelpers';
import { validateLinkUrlInput } from '@utils/linkUrlValidation.jsx';

function consultationEditorHrefMatchesStored(storedUrl, editorHref) {
    const a = String(storedUrl || '').trim();
    const b = String(editorHref || '').trim();
    if (!a || !b) return false;
    if (a === b) return true;
    try {
        return normalizePhaseLinkUrl(a) === normalizePhaseLinkUrl(b);
    } catch {
        return false;
    }
}

function consultationLinkStillInHtml(html, url) {
    const u = String(url || '').trim();
    if (!u || !html || typeof html !== 'string') return false;
    if (html.includes(u)) return true;
    try {
        const n = normalizePhaseLinkUrl(u);
        if (n && html.includes(n)) return true;
    } catch {
        /* ignore */
    }
    return false;
}

export function useProjectTaskConsultation(projectId) {
    const queryClient = useQueryClient();
    const [open, setOpen] = useState(false);
    const [submittedItems, setSubmittedItems] = useState([]);
    const [consultationNotes, setConsultationNotes] = useState('');
    const [consultantIds, setConsultantIds] = useState([]);
    const [qaPassed, setQaPassed] = useState(false);
    
    const [consultationPendingFiles, setConsultationPendingFiles] = useState([]);
    const [consultationPendingLinks, setConsultationPendingLinks] = useState([]);
    const [consultationLinkModalOpen, setConsultationLinkModalOpen] = useState(false);
    const [consultationLinkForm] = Form.useForm();
    const consultationLinkCallbackRef = useRef(null);

    useEffect(() => {
        const html = consultationNotes;
        if (!html || typeof html !== 'string') {
            setConsultationPendingLinks([]);
            return;
        }
        setConsultationPendingLinks((prev) =>
            prev.filter((x) => !x?.url || consultationLinkStillInHtml(html, x.url)),
        );
    }, [consultationNotes]);

    const openConsultationModal = useCallback(() => setOpen(true), []);

    const addToConsultation = useCallback((item) => {
        setSubmittedItems((prev) => {
            const keyOf = (i) => `${i.type}:${String(i.id)}`;
            const itemKey = keyOf(item);
            if (prev.some((i) => keyOf(i) === itemKey)) return prev;
            return [...prev, item];
        });
        setOpen(true);
    }, []);

    const removeFromConsultation = useCallback((type, id) => {
        setSubmittedItems((prev) => {
            const matchId = (a, b) => (a == null && b == null ? true : String(a) === String(b));
            return prev.filter((i) => i.type !== type || !matchId(i.id, id));
        });
    }, []);

    const clearConsultation = useCallback(() => {
        setSubmittedItems([]);
        setConsultationNotes('');
        setConsultantIds([]);
        setQaPassed(false);
        setConsultationPendingFiles((prev) => {
            prev.forEach((p) => {
                if (p?.preview) {
                    try {
                        URL.revokeObjectURL(p.preview);
                    } catch {
                        /* ignore */
                    }
                }
            });
            return [];
        });
        setConsultationPendingLinks([]);
    }, []);

    const closeConsultationModal = useCallback(() => {
        setConsultationLinkModalOpen(false);
        consultationLinkCallbackRef.current = null;
        try {
            consultationLinkForm.resetFields();
        } catch {
            /* ignore */
        }
        setOpen(false);
        clearConsultation();
    }, [clearConsultation, consultationLinkForm]);

    const removeConsultationPendingFile = useCallback((id) => {
        setConsultationPendingFiles((prev) => {
            const index = prev.findIndex((p) => p.id === id);
            const removedAt = index >= 0 ? prev[index] : null;
            if (removedAt?.preview) {
                try {
                    URL.revokeObjectURL(removedAt.preview);
                } catch {
                    /* ignore */
                }
            }
            if (index < 0) return prev.filter((p) => p.id !== id);
            setConsultationNotes((content) => {
                let c = content || '';
                const linkRegex = new RegExp(`<a\\s(?=[^>]*href="#attachment-${index}")[^>]*>[\\s\\S]*?</a>`, 'gi');
                c = c.replace(linkRegex, '');
                for (let j = prev.length - 1; j > index; j--) {
                    c = c.replace(new RegExp(`#attachment-${j}`, 'g'), `#attachment-${j - 1}`);
                }
                return c;
            });
            return prev.filter((p) => p.id !== id);
        });
    }, []);

    const removeConsultationPendingLink = useCallback((id) => {
        setConsultationPendingLinks((prev) => {
            const removed = prev.find((p) => p.id === id);
            const next = prev.filter((p) => p.id !== id);
            if (removed?.url) {
                const href = String(removed.url);
                const esc = href.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                setConsultationNotes((content) => {
                    const c = String(content || '');
                    const re = new RegExp(`<a\\s(?=[^>]*href=["']${esc}["'])[^>]*>[\\s\\S]*?<\\/a>`, 'i');
                    return c.replace(re, '');
                });
            }
            return next;
        });
    }, []);

    const handleConsultationFilesSelected = useCallback((files) => {
        if (!files?.length) return;
        const next = files.map((file, i) => {
            const isImage = (file.type || '').startsWith('image/');
            return {
                id: `cons-${Date.now()}-${i}`,
                file,
                name: file.name,
                type: file.type,
                preview: isImage ? URL.createObjectURL(file) : null,
            };
        });
        setConsultationPendingFiles((prev) => [...prev, ...next]);
    }, []);

    const handleConsultationLinkRequest = useCallback((insertLink) => {
        consultationLinkCallbackRef.current = insertLink;
        consultationLinkForm.resetFields();
        setConsultationLinkModalOpen(true);
    }, [consultationLinkForm]);

    const closeConsultationLinkModal = useCallback(() => {
        setConsultationLinkModalOpen(false);
        consultationLinkCallbackRef.current = null;
    }, []);

    const handleConsultationLinkModalSubmit = useCallback(() => {
        consultationLinkForm.validateFields().then((values) => {
            const rawUrl = (values.url || '').trim();
            const title = (values.title || '').trim();
            const check = validateLinkUrlInput(rawUrl);
            if (!check.ok) {
                consultationLinkForm.setFields([{ name: 'url', errors: [check.message || 'Enter a valid URL'] }]);
                return;
            }
            const normalizedUrl = check.normalized;
            const displayTitle = title || normalizedUrl;
            const insertLink = consultationLinkCallbackRef.current;
            if (insertLink) insertLink({ url: normalizedUrl, title: displayTitle });
            setConsultationPendingLinks((prev) => [
                ...prev,
                { id: `link-${Date.now()}`, url: normalizedUrl, title: displayTitle },
            ]);
            setConsultationLinkModalOpen(false);
            consultationLinkCallbackRef.current = null;
        });
    }, [consultationLinkForm]);

    const handleConsultationEditorLinkRemoved = useCallback((href) => {
        if (!href) return;
        setConsultationPendingLinks((prev) => prev.filter((p) => !consultationEditorHrefMatchesStored(p.url, href)));
    }, []);

    const buildConsultationPayload = useCallback((opts = {}) => {
        const taskIds = submittedItems.filter((i) => i.type === 'task').map((i) => String(i.id));
        const notesRaw = (opts.notes ?? consultationNotes).trim();
        let notesMarkdown = '';
        if (notesRaw) {
            try {
                const forApi = contentToApiMentions(notesRaw);
                notesMarkdown = htmlToMarkdown(forApi).trim();
            } catch {
                notesMarkdown = String(notesRaw).trim();
            }
        }
        
        const linkAttachments = consultationPendingLinks.map(({ url, title }) => ({
            type: 'link',
            url,
            title: title || url,
        }));

        if (opts.files?.length) {
            const fd = new FormData();
            taskIds.forEach(id => fd.append('taskIds[]', id));
            consultantIds.forEach(id => fd.append('consultantIds[]', id));
            fd.append('qaPassed', String(qaPassed));
            if (notesMarkdown) fd.append('description', JSON.stringify({ markdown: notesMarkdown }));
            if (linkAttachments.length > 0) fd.append('attachmentUrls', JSON.stringify(linkAttachments));
            opts.files.forEach(f => fd.append('files', f));
            return fd;
        }

        return {
            taskIds,
            consultantIds,
            qaPassed,
            description: notesMarkdown ? { markdown: notesMarkdown } : undefined,
            attachments: linkAttachments.length > 0 ? linkAttachments : undefined,
        };
    }, [submittedItems, consultationNotes, consultationPendingLinks, consultantIds, qaPassed]);

    const mutation = useMutation({
        mutationFn: (opts) => createTaskConsultation(projectId, buildConsultationPayload(opts)),
        onSuccess: () => {
            invalidateProjectQueries(queryClient, projectId);
        },
    });

    const submitConsultation = useCallback((callbacks = {}) => {
        if (submittedItems.length === 0) {
            message.warning('Select at least one task');
            return;
        }
        if (consultantIds.length === 0) {
            message.warning('Select at least one consultant');
            return;
        }
        
        const { onSuccess, onError } = callbacks;
        const files = consultationPendingFiles.map((p) => p.file);
        
        mutation.mutate(
            { files: files.length > 0 ? files : undefined },
            {
                onSuccess: () => {
                    message.success('Consultation created');
                    closeConsultationModal();
                    onSuccess?.();
                },
                onError: (err) => {
                    message.error(err?.response?.data?.message || err?.message || 'Failed to create consultation');
                    onError?.(err);
                },
            }
        );
    }, [submittedItems.length, consultantIds.length, consultationPendingFiles, mutation, closeConsultationModal]);

    const submissionGroupedBySprintEnriched = useMemo(() => {
        const bySprint = new Map();
        submittedItems.forEach((i) => {
            const sid = i.sprintId;
            if (sid == null) return;
            if (!bySprint.has(sid)) {
                bySprint.set(sid, {
                    sprintId: sid,
                    fullSprint: i.fullSprint,
                    fullTasks: [],
                });
            }
            const row = bySprint.get(sid);
            row.fullTasks.push(i.fullTask);
        });
        return Array.from(bySprint.values());
    }, [submittedItems]);

    return {
        open,
        openConsultationModal,
        onCancel: closeConsultationModal,
        submittedItems,
        addToConsultation,
        removeFromConsultation,
        consultationNotes,
        setConsultationNotes,
        consultantIds,
        setConsultantIds,
        qaPassed,
        setQaPassed,
        consultationPendingFiles,
        consultationPendingLinks,
        consultationLinkModalOpen,
        consultationLinkForm,
        handleConsultationFilesSelected,
        handleConsultationLinkRequest,
        handleConsultationLinkModalSubmit,
        closeConsultationLinkModal,
        removeConsultationPendingFile,
        removeConsultationPendingLink,
        handleConsultationEditorLinkRemoved,
        submitConsultation,
        submitPending: mutation.isPending,
        submissionGroupedBySprintEnriched,
    };
}
